"use client";

import { useUserTeamForTournament } from "@/hooks";
import { getSportIcon } from "@/lib/sports/icons";
import { Location, Organizer, Tournament } from "@/types";
import { Icon } from "@iconify/react";
import { formatInTimeZone } from "date-fns-tz";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

interface TournamentCardProps {
  tournament: Tournament;
  organizer: Organizer;
  locations?: Location[];
  teamCount?: number;
  playerCount?: number;
  matchCount?: number;
}

export function TournamentCard({
  tournament,
  organizer,
  locations,
  teamCount = 0,
  playerCount = 0,
  matchCount = 0,
}: TournamentCardProps) {
  const t = useTranslations();
  const { data: userTeam } = useUserTeamForTournament(tournament.id);
  const isAttending = !!userTeam;
  // Prevent hydration mismatch by deferring time-based calculations
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Check if sign-in is currently open - only after mount to prevent hydration mismatch
  const now = mounted ? new Date() : null;
  const signInStartsAt = tournament.signInStartsAt
    ? new Date(tournament.signInStartsAt)
    : null;
  const signInEndsAt = tournament.signInEndsAt
    ? new Date(tournament.signInEndsAt)
    : null;

  const isSignInOpen = now
    ? (!signInStartsAt || now >= signInStartsAt) &&
      (!signInEndsAt || now <= signInEndsAt)
    : false;

  // Check if tournament has ended - only after mount
  const tournamentEnded = now ? now > new Date(tournament.endsAt) : false;

  // Check if tournament is currently active (started but not ended)
  const isTournamentActive = now
    ? now >= new Date(tournament.startsAt) && !tournamentEnded
    : false;

  return (
    <Link
      href={`/tournaments/${tournament.slug}`}
      className="group relative w-full flex flex-col overflow-hidden rounded-2xl border border-[var(--border-card)] bg-[var(--bg-card)] transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-[var(--border-gold)] hover:shadow-[var(--shadow-hover)]"
    >
      {/* Status badges - only render after mount to prevent hydration mismatch */}
      {mounted && (
        <>
          {/* Tournament ended badge */}
          {tournamentEnded && (
            <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 backdrop-blur-md">
              <Icon
                icon="solar:check-circle-bold"
                className="h-3 w-3 text-default-400"
              />
              <span className="font-cinzel text-xs font-semibold uppercase tracking-wider text-default-400">
                {t("tournaments.ended")}
              </span>
            </div>
          )}

          {/* Attending badge */}
          {!tournamentEnded && isAttending && (
            <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 backdrop-blur-md">
              <span className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
              <span className="font-cinzel text-xs font-semibold uppercase tracking-wider text-blue-400">
                {t("tournaments.attending")}
              </span>
            </div>
          )}

          {/* Active tournament badge (started, not ended, sign-in closed) */}
          {!tournamentEnded &&
            !isAttending &&
            isTournamentActive &&
            !isSignInOpen && (
              <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 backdrop-blur-md">
                <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                <span className="font-cinzel text-xs font-semibold uppercase tracking-wider text-emerald-400">
                  {t("tournaments.active")}
                </span>
              </div>
            )}

          {/* Sign-in open badge - rendered between image and content below */}

          {/* Sign-in date range badge (warning color when not open yet, only show when tournament hasn't started yet) */}
          {!tournamentEnded &&
            !isAttending &&
            !isSignInOpen &&
            !isTournamentActive &&
            (signInStartsAt || signInEndsAt) && (
              <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 backdrop-blur-md">
                <Icon
                  icon="solar:pen-new-square-bold"
                  className="h-3 w-3 text-amber-400"
                />
                <span className="font-cinzel text-xs font-semibold uppercase tracking-wider text-amber-400">
                  {t("tournaments.signInDates")}:{" "}
                  {signInStartsAt && formatInTimeZone(signInStartsAt, "Europe/Helsinki", "d.M.")}
                  {signInStartsAt && signInEndsAt && " - "}
                  {signInEndsAt && formatInTimeZone(signInEndsAt, "Europe/Helsinki", "d.M.")}
                </span>
              </div>
            )}
        </>
      )}

      {/* Tournament header image / logo area */}
      <div className="relative flex aspect-[8/5] items-center justify-center overflow-hidden bg-gradient-to-br from-[var(--bg-primary)] to-[var(--bg-deep)] p-4">
        {tournament.headerImage ? (
          <Image
            src={tournament.headerImage}
            alt=""
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, 400px"
          />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-[var(--border-gold)] bg-gradient-to-br from-[var(--gold-dark)] to-[var(--gold)] shadow-lg">
            <Icon
              icon="game-icons:stone-tower"
              className="h-8 w-8 text-[var(--bg-deep)]"
            />
          </div>
        )}

        {/* Shimmer overlay */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      </div>

      {/* Sign-in open banner */}
      {mounted && !tournamentEnded && !isAttending && isSignInOpen && (
        <div className="flex items-center justify-center gap-2 bg-emerald-600 px-3 py-1.5">
          <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
          <span className="font-cinzel text-sm font-semibold uppercase tracking-wider text-white">
            {t("tournaments.signInOpen", {
              date: signInEndsAt ? formatInTimeZone(signInEndsAt, "Europe/Helsinki", "d.M. HH:mm") : "",
            })}
          </span>
        </div>
      )}

      {/* Content */}
      <div className="flex flex-1 flex-col p-5">
        {/* Tournament name */}
        <h3 className="mb-3 font-cinzel text-xl font-bold leading-tight tracking-wide text-primary transition-colors group-hover:text-[var(--gold)]">
          {tournament.name}
        </h3>
        <div className="flex flex-col gap-1 mb-4">
          {/* Sport */}
          <div className="flex items-center gap-2.5">
            <Icon icon={getSportIcon(tournament.sport)} className="h-4 w-4" />
            <span className="font-spectral text-sm">
              {t(`sports.${tournament.sport}`)}
            </span>
          </div>

          {/* Organizer */}
          <div className="flex items-center gap-2.5">
            <Icon icon="game-icons:fairy-wand" className="h-4 w-4" />
            <span className="font-spectral text-sm">{organizer.name}</span>
          </div>

          {/* Date indicator */}
          <div className="flex items-center gap-2">
            <Icon icon="game-icons:calendar" className="h-4 w-4" />
            <span className="font-spectral text-sm">
              {formatInTimeZone(tournament.startsAt, "Europe/Helsinki", "d.M.")} -{" "}
              {formatInTimeZone(tournament.endsAt, "Europe/Helsinki", "d.M.yyyy")}
            </span>
          </div>

          {/* Locations */}
          {locations && locations.length > 0 && (
            <div className="flex items-start gap-2">
              <Icon
                icon="game-icons:position-marker"
                className="mt-0.5 h-4 w-4 flex-shrink-0"
              />
              <span className="font-spectral text-sm leading-relaxed">
                {locations.map((location, idx) => (
                  <span key={location.id}>
                    {location.name}
                    {idx < locations.length - 1 && ", "}
                  </span>
                ))}
              </span>
            </div>
          )}
        </div>

        {/* Description */}
        {tournament.description && (
          <p className="mb-4 font-spectral text-sm leading-relaxed line-clamp-3 whitespace-pre-line">
            {tournament.description}
          </p>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-3 border-t border-[var(--border-subtle)] pt-4">
          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-1.5">
              <Icon
                icon="majesticons:users"
                className="h-4 w-4 text-[var(--gold)]"
              />
              <span className="font-cinzel text-base font-bold text-primary tabular-nums">
                {teamCount}
              </span>
            </div>
            <span className="font-spectral text-xs text-muted">
              {t("statistics.teams", { count: teamCount })}
            </span>
          </div>

          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-1.5">
              <Icon
                icon="ph:users-four-fill"
                className="h-4 w-4 text-[var(--gold)]"
              />
              <span className="font-cinzel text-base font-bold text-primary tabular-nums">
                {playerCount}
              </span>
            </div>
            <span className="font-spectral text-xs text-muted">
              {t("statistics.players", { count: playerCount })}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
